import { ArrowBackRounded } from '@mui/icons-material'
import { Box, Button, IconButton, Typography } from '@mui/material'
import React, { FC, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import ErrorBoundary from '../ErrorBoundary'
import { CustomersClient } from '../interactors/clients/CustomerClient'
import { CustomerDto } from '../interactors/gen/backendClient'
import { useAccountsStore } from '../store/AccountsStore'
import { useDashboardLoaderStore } from '../store/DashboardLoaderStore'
import { FreeTrialBanner } from './FreeTrialBanner'
import { getDashboardPageContext, ResponsiveAppBar } from './ResponsiveAppBar'
import { CircularProgressPanel } from './common/CircularProgressPanel'
import { colors } from './theme'
import { Avatar, Stack } from '@mui/material'
import { PATHS } from './PATHS'
import { getLegacyInterfaceUrl } from '../utils/legacyInterface'
import { getCustomerProfilePhotoSrc } from '../utils/S3-links'
import { addSecondaryFabSx } from './common/addPrimaryCtaSx'

const FREE_TRIAL_BANNER_STORAGE_KEY = 'petso_freeTrialBannerFirstSeenAt'
const FREE_TRIAL_BANNER_DURATION_MS = 7 * 24 * 60 * 60 * 1000

const getFreeTrialBannerStorageKey = (customerId: string, accountId?: string | null) => {
  if (accountId) {
    return `${FREE_TRIAL_BANNER_STORAGE_KEY}:${accountId}`
  }
  return `${FREE_TRIAL_BANNER_STORAGE_KEY}:${customerId}`
}

export const DashboardStructure: FC = () => {
  const { isLoading } = useDashboardLoaderStore()

  const accountsStore = useAccountsStore()
  const location = useLocation()
  const connectedAccountId = accountsStore.connectedAccount?.id

  const [customer, setCustomer] = React.useState<CustomerDto | null>(null)
  const [shouldShowFreeTrialBanner, setShouldShowFreeTrialBanner] = React.useState(false)
  const pageContext = getDashboardPageContext(location.pathname, location.search, customer?.types)
  const isAssociationRoot = location.pathname === PATHS.association.absolute
  const customerLogoSrc = customer?.id
    ? `https://logo-customer-images.s3.eu-west-3.amazonaws.com/${customer.id}.jpg`
    : ''
  const avatarSrc =
    (customer?.profileImageKey && getCustomerProfilePhotoSrc(customer.profileImageKey)) || customerLogoSrc || undefined

  useEffect(() => {
    const fetchCustomer = async () => {
      const customer = await CustomersClient.getCurrentCustomer()
      accountsStore.changeConnectedCustomer(customer)
      setCustomer(customer)
    }

    fetchCustomer()
  }, [accountsStore.changeConnectedAccount])

  const isFreeTrialBannerLocationAllowed = location.pathname !== '/suivi-adoptant/ajouter-adoptant'

  useEffect(() => {
    if (!isFreeTrialBannerLocationAllowed || !customer?.id) {
      setShouldShowFreeTrialBanner(false)
      return
    }

    const storageKey = getFreeTrialBannerStorageKey(customer.id, connectedAccountId)
    const now = Date.now()

    try {
      const storedValue = localStorage.getItem(storageKey)
      if (!storedValue) {
        localStorage.setItem(storageKey, String(now))
        setShouldShowFreeTrialBanner(true)
        return
      }

      const firstSeenAt = Number(storedValue)
      if (!Number.isFinite(firstSeenAt)) {
        localStorage.setItem(storageKey, String(now))
        setShouldShowFreeTrialBanner(true)
        return
      }

      setShouldShowFreeTrialBanner(now - firstSeenAt < FREE_TRIAL_BANNER_DURATION_MS)
    } catch (error) {
      setShouldShowFreeTrialBanner(true)
    }
  }, [customer?.id, connectedAccountId, isFreeTrialBannerLocationAllowed])

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          maxHeight: '100vh',
          overflow: 'hidden',
          backgroundColor: '#F6F7FB',
          gap: { md: 0 },
          p: { md: 1.25 },
          boxSizing: 'border-box',
        }}
      >
        <ResponsiveAppBar customerTypes={customer?.types} />

        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            paddingTop: { xs: '64px', md: 0 },
            marginLeft: { md: '-1px' },
          }}
        >
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 2,
              px: { md: 2.5, lg: 3 },
              py: 1.45,
              mb: 1,
              minHeight: 66,
              border: '1px solid #E4E8F2',
              borderLeft: 'none',
              borderRadius: '0 28px 28px 0',
              backgroundColor: '#EEF2F8',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flexGrow: 1 }}>
              {pageContext.showBackButton && (
                <IconButton onClick={() => window.history.back()} sx={{ color: colors.cello }}>
                  <ArrowBackRounded />
                </IconButton>
              )}
              {isAssociationRoot ? (
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                  <Avatar src={avatarSrc} sx={{ width: 36, height: 36 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: colors.cello, fontSize: '0.95rem', fontWeight: 800, lineHeight: 1.1 }}>
                      Mon asso
                    </Typography>
                    <Typography sx={{ color: '#7A8197', fontSize: '0.9rem', fontWeight: 500, lineHeight: 1.1 }} noWrap>
                      {customer?.name}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: colors.cello,
                      fontSize: { md: '0.96rem', lg: '1rem' },
                      fontWeight: 700,
                      lineHeight: 1.1,
                    }}
                  >
                    {pageContext.title}
                  </Typography>
                </Box>
              )}
            </Box>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexShrink: 0 }}>
              <Typography sx={{ color: '#7A8197', fontSize: '0.82rem', fontWeight: 600 }}>Nostalgique ?</Typography>
              <Button
                variant="outlined"
                href={getLegacyInterfaceUrl()}
                target="_blank"
                rel="noreferrer"
                sx={{
                  ...addSecondaryFabSx,
                  minHeight: 34,
                  height: 34,
                  px: 1.65,
                  fontSize: 12.5,
                }}
              >
                Accéder à l&apos;ancienne interface
              </Button>
            </Stack>
          </Box>

          <Box className="scrollable-content" sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
            {isFreeTrialBannerLocationAllowed && shouldShowFreeTrialBanner && <FreeTrialBanner />}
            {isLoading && <CircularProgressPanel />}
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </Box>
        </Box>
      </Box>
    </>
  )
}
